export const LOGICAL_WIDTH = 1280;
export const LOGICAL_HEIGHT = 720;

export interface ViewportMetrics {
  viewportWidth: number;
  viewportHeight: number;
  scale: number;
  scaledWidth: number;
  scaledHeight: number;
}

export function calculateViewport(viewportWidth: number, viewportHeight: number): ViewportMetrics {
  const scale = Math.min(viewportWidth / LOGICAL_WIDTH, viewportHeight / LOGICAL_HEIGHT);
  return {
    viewportWidth,
    viewportHeight,
    scale,
    scaledWidth: LOGICAL_WIDTH * scale,
    scaledHeight: LOGICAL_HEIGHT * scale,
  };
}

export class ViewportController {
  private readonly onResize = (): void => this.update();

  constructor(
    private readonly stage: HTMLElement,
    private readonly scaler: HTMLElement,
    private readonly rotateOverlay: HTMLElement,
    private readonly debug?: HTMLElement,
  ) {
    window.addEventListener("resize", this.onResize, { passive: true });
    window.addEventListener("orientationchange", this.onResize, { passive: true });
    this.update();
  }

  update(): void {
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const metrics = calculateViewport(viewportWidth, viewportHeight);
    const portrait = viewportWidth <= viewportHeight;
    this.stage.style.setProperty("--viewport-scale", String(metrics.scale));
    this.scaler.style.setProperty("--scaled-width", `${metrics.scaledWidth}px`);
    this.scaler.style.setProperty("--scaled-height", `${metrics.scaledHeight}px`);
    this.scaler.hidden = portrait;
    this.rotateOverlay.hidden = !portrait;
    this.rotateOverlay.setAttribute("aria-hidden", String(!portrait));
    if (this.debug) {
      this.debug.hidden = false;
      this.debug.textContent = [
        `viewport: ${metrics.viewportWidth} x ${metrics.viewportHeight}`,
        `scale: ${metrics.scale.toFixed(4)}`,
        `logical: ${LOGICAL_WIDTH} x ${LOGICAL_HEIGHT}`,
        `scaled: ${metrics.scaledWidth.toFixed(3)} x ${metrics.scaledHeight.toFixed(3)}`,
      ].join("\n");
    }
  }

  destroy(): void {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("orientationchange", this.onResize);
  }
}
